<template>
	<div class="ai-assistant">
		<!-- 浮动按钮 -->
		<div
			ref="floatingButtonRef"
			class="ai-float-button"
			:class="{ 'ai-float-button--active': isOpen }"
			:style="{ left: buttonPosition.x + 'px', bottom: buttonPosition.y + 'px' }"
			@mousedown="startDrag"
			@click="handleButtonClick"
		>
			<el-icon :size="24" class="ai-icon">
				<ChatDotRound v-if="!isOpen" />
				<Close v-else />
			</el-icon>
		</div>

		<!-- 悬浮提示框 -->
		<div
			v-if="showFloatingTip && !isOpen && messages.length <= 1"
			ref="floatingTipRef"
			class="ai-floating-tip"
			:style="{ left: buttonPosition.x + 80 + 'px', bottom: buttonPosition.y + 10 + 'px' }"
			@click="handleTipClick"
		>
			<div class="ai-floating-tip__content">
				<div class="ai-floating-tip__icon">
					<el-icon :size="14">
						<ChatDotRound />
					</el-icon>
				</div>
				<div class="ai-floating-tip__text">{{ currentQuestion }}</div>
				<div class="ai-floating-tip__close" @click.stop="handleCloseTip">
					<el-icon :size="12">
						<Close />
					</el-icon>
				</div>
			</div>
			<div class="ai-floating-tip__arrow"></div>
		</div>

		<!-- 对话弹窗 -->
		<div v-if="isOpen" class="ai-dialog" :class="{ 'ai-dialog--show': isOpen }">
			<!-- 历史记录面板 -->
			<div class="ai-history-panel">
				<div class="ai-history-header">
					<div class="ai-history-title">
						<el-icon :size="16">
							<Clock />
						</el-icon>
						<span>{{ t('componentsModule.aiAssistant.history') }}</span>
					</div>
				</div>
				<div class="ai-new-chat" @click="startNewChat">
					<el-icon :size="14">
						<Plus />
					</el-icon>
					<span>{{ t('componentsModule.aiAssistant.newChat') }}</span>
				</div>
				<div ref="historyListRef" class="ai-history-list" @scroll="handleHistoryScroll">
					<div
						v-for="history in chatHistories"
						:key="history.id"
						class="ai-history-item"
						:class="{ 'ai-history-item--active': history.id === currentChatId }"
						@click="loadChatHistory(history)"
					>
						<div class="ai-history-item-title">{{ history.title }}</div>
						<div class="ai-history-item-time">{{ formatHistoryTime(history.timestamp) }}</div>
					</div>
					<div v-if="isLoadingMore && hasMoreData" class="ai-history-loading">
						<div class="ai-loading-dots">
							<span></span>
							<span></span>
							<span></span>
						</div>
						<div class="ai-loading-text">{{ t('componentsModule.aiAssistant.loading') }}</div>
					</div>
					<div v-if="!hasMoreData && chatHistories.length > 0" class="ai-history-end">{{ t('componentsModule.aiAssistant.noMoreHistory') }}</div>
					<div v-if="chatHistories.length === 0" class="ai-history-empty">{{ t('componentsModule.aiAssistant.noHistory') }}</div>
				</div>
			</div>

			<!-- 对话内容区域 -->
			<div class="ai-dialog__main">
				<div class="ai-dialog__header">
					<div class="ai-dialog__title">
						<el-icon :size="20" class="ai-title-icon">
							<ChatDotRound />
						</el-icon>
						<span>{{ t('componentsModule.aiAssistant.title') }}</span>
					</div>
					<div class="ai-model-selector" @click.stop="toggleModelSelector">
						<div class="ai-model-current">
							<span class="ai-model-type-icon">
								<el-icon v-if="currentModel.type === 'cloud'"><Connection /></el-icon>
								<el-icon v-else><Cpu /></el-icon>
							</span>
							<span class="ai-model-name">{{ currentModel.name }}</span>
							<el-icon class="ai-model-arrow" :class="{ 'is-rotated': showModelSelector }">
								<ArrowDown />
							</el-icon>
						</div>
						<transition name="el-zoom-in-top">
							<div v-show="showModelSelector" class="ai-model-dropdown">
								<div class="ai-model-dropdown-header">{{ t('componentsModule.aiAssistant.selectModel') }}</div>
								<div
									v-for="model in modelList"
									:key="model.id"
									class="ai-model-option"
									:class="{ 'is-selected': currentModel.id === model.id }"
									@click.stop="selectModel(model)"
								>
									<div class="ai-model-option-content">
										<div class="ai-model-option-top">
											<span class="ai-model-option-name">{{ model.name }}</span>
											<el-icon v-if="currentModel.id === model.id" class="ai-model-check">
												<Check />
											</el-icon>
										</div>
										<div class="ai-model-desc">{{ model.description }}</div>
										<div class="ai-model-skills">
											<span v-for="skill in model.skills" :key="skill" class="ai-skill-tag">{{ skill }}</span>
										</div>
									</div>
								</div>
							</div>
						</transition>
					</div>
					<div class="ai-dialog__actions">
						<div
							class="ai-dialog__reset"
							:class="{ 'ai-dialog__reset--disabled': isTyping }"
							:title="t('componentsModule.aiAssistant.resetChat')"
							@click="resetChat"
						>
							<el-icon :size="16">
								<RefreshLeft />
							</el-icon>
						</div>
						<div class="ai-dialog__close" @click="toggleDialog">
							<el-icon :size="18">
								<Close />
							</el-icon>
						</div>
					</div>
				</div>

				<!-- 推荐问题区域 - 固定在顶端 -->
				<div class="ai-preset-questions-fixed">
					<div class="ai-preset-header">
						<div class="ai-preset-title">
							<el-icon :size="14">
								<QuestionFilled />
							</el-icon>
							<span>{{ t('componentsModule.aiAssistant.recommendedQuestions') }}</span>
							<div v-if="isLoadingQuestions" class="ai-loading-dots">
								<span></span>
								<span></span>
								<span></span>
							</div>
						</div>
						<div class="ai-preset-toggle" :class="{ 'ai-preset-toggle--collapsed': !showPresetQuestions }" @click="togglePresetQuestions">
							<el-icon :size="12">
								<ArrowDown />
							</el-icon>
						</div>
					</div>
					<div v-show="showPresetQuestions" class="ai-preset-content">
						<!-- 加载状态 -->
						<div v-if="isLoadingQuestions" class="ai-preset-loading">
							<div v-for="i in presetQuestions" :key="i" class="ai-preset-skeleton"></div>
						</div>
						<!-- 问题列表 -->
						<div v-else class="ai-preset-flow">
							<div
								v-for="(question, index) in presetQuestions"
								:key="index"
								class="ai-preset-card"
								:class="`ai-preset-card--${index % 3}`"
								:disabled="isTyping"
								@click="handlePresetQuestion(question)"
							>
								<div class="ai-preset-card-text">{{ question }}</div>
								<div class="ai-preset-card-arrow">
									<el-icon :size="12">
										<ArrowRight />
									</el-icon>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div ref="chatContainer" class="ai-dialog__content">
					<div v-for="(message, index) in messages" :key="index" class="ai-message" :class="`ai-message--${message.sender}`">
						<div class="ai-message__avatar">
							<el-icon :size="16">
								<User v-if="message.sender === 'user'" />
								<Avatar v-else />
							</el-icon>
						</div>
						<div class="ai-message__content">
							<div v-if="message.thinking" class="ai-message__thinking">
								<div class="ai-message__thinking-label" @click="toggleThinking(message)">
									<el-icon class="ai-thinking-icon" :class="{ 'is-collapsed': message.thinkingCollapsed }">
										<ArrowDown />
									</el-icon>
									<span>深度思考</span>
								</div>
								<div v-show="!message.thinkingCollapsed" class="ai-message__thinking-content" v-html="formatMessage(message.thinking)"></div>
							</div>
							<div class="ai-message__text" v-html="formatMessage(message.content)"></div>
							<div class="ai-message__time">{{ formatTime(message.timestamp) }}</div>
						</div>
					</div>

					<div v-if="shouldShowLoadingBubble" class="ai-message ai-message--ai">
						<div class="ai-message__avatar">
							<el-icon :size="16">
								<Avatar />
							</el-icon>
						</div>
						<div class="ai-message__content">
							<div class="ai-typing-indicator">
								<span></span>
								<span></span>
								<span></span>
							</div>
							<div class="ai-message__time">{{ t('componentsModule.aiAssistant.thinking') }}</div>
						</div>
					</div>
				</div>

				<div class="ai-dialog__footer">
					<div class="ai-input-wrapper">
						<el-input
							v-model="inputText"
							type="textarea"
							:rows="2"
							:maxlength="2000"
							show-word-limit
							:placeholder="t('componentsModule.aiAssistant.inputPlaceholder')"
							:disabled="isTyping"
							resize="none"
							@keydown.enter.prevent="handleKeyDown"
						/>
						<el-button type="primary" :loading="isTyping" :disabled="!inputText.trim() || isTyping" class="ai-send-btn" @click="sendMessage">
							<el-icon>
								<Promotion />
							</el-icon>
						</el-button>
					</div>
				</div>
			</div>
		</div>
		<div v-if="isOpen" class="ai-overlay"></div>
	</div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, computed, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/modules/user'
const userStore = useUserStore()
const { t } = useI18n()
import {
	ChatDotRound,
	Close,
	User,
	Avatar,
	Promotion,
	RefreshLeft,
	Clock,
	Plus,
	Delete,
	QuestionFilled,
	ArrowDown,
	Tools,
	ArrowRight,
	Check,
	Cpu,
	Connection
} from '@element-plus/icons-vue'
import { apiRequest } from '@/api/common'

interface Message {
	sender: 'user' | 'ai'
	content: string
	thinking?: string
	thinkingCollapsed?: boolean
	timestamp: number
}

interface ChatHistory {
	id: string
	title: string
	messages: Message[]
	timestamp: number
}

type ChatCompletionHistoryItem = {
	role: 'user' | 'assistant'
	content: string
}

// 原有状态
const isOpen = ref(false)
const inputText = ref('')

// 模型选择相关
interface ModelOption {
	id: string
	name: string
	type: 'cloud' | 'local'
	skills: string[]
	description: string
}

const modelList: ModelOption[] = [
	{
		id: 'deepseek-r1',
		name: '高泛化诊断大模型',
		type: 'cloud',
		skills: [],
		description: ''
	},
	{
		id: 'local-v3',
		name: 'PHM垂直大模型',
		type: 'local',
		skills: [],
		description: ''
	}
]

const currentModel = ref<ModelOption>(modelList[0])
const showModelSelector = ref(false)

const toggleModelSelector = () => {
	showModelSelector.value = !showModelSelector.value
}

const selectModel = (model: ModelOption) => {
	currentModel.value = model
	showModelSelector.value = false
}
const messages = ref<Message[]>([
	{
		sender: 'ai',
		content: t('componentsModule.aiAssistant.welcomeMessage'),
		timestamp: Date.now()
	}
])
const isTyping = ref(false)
const chatContainer = ref<HTMLElement>()

// 是否显示加载气泡
const shouldShowLoadingBubble = computed(() => {
	if (!isTyping.value) {
		return false
	}
	const lastMsg = messages.value[messages.value.length - 1]
	if (lastMsg && lastMsg.sender === 'ai' && (lastMsg.content || lastMsg.thinking)) {
		return false
	}
	return true
})

// 历史记录相关状态
const chatHistories = ref<ChatHistory[]>([])
const currentChatId = ref<string>('')
const showHistoryPanel = ref(true)

// 推荐问题显示状态
const showPresetQuestions = ref(true)
const isLoadingQuestions = ref(false)

// 默认推荐问题
const DEFAULT_PRESET_QUESTIONS = [
	t('componentsModule.aiAssistant.question1'),
	t('componentsModule.aiAssistant.question2'),
	t('componentsModule.aiAssistant.question3'),
	t('componentsModule.aiAssistant.question4'),
	t('componentsModule.aiAssistant.question5')
]

// 预设问题
const presetQuestions = ref([...DEFAULT_PRESET_QUESTIONS])

// 拖拽相关状态
const isDragging = ref(false)

const buildChatCompletionHistory = (messageList: Message[], maxItems = 20): ChatCompletionHistoryItem[] => {
	const items: ChatCompletionHistoryItem[] = messageList
		.map(m => ({
			role: m.sender === 'user' ? 'user' : 'assistant',
			content: String(m.content ?? '')
		}))
		.filter(item => item.content !== '')

	while (items.length && items[0].role === 'assistant') {
		items.shift()
	}

	while (items.length && items[items.length - 1].role === 'user') {
		items.pop()
	}

	if (items.length > maxItems) {
		return items.slice(items.length - maxItems)
	}
	return items
}
const buttonPosition = ref({ x: 20, y: 20 })
const dragStart = ref({ x: 0, y: 0 })
const mouseStart = ref({ x: 0, y: 0 })

// DOM引用
const floatingTipRef = ref<HTMLElement | null>(null)
const floatingButtonRef = ref<HTMLElement | null>(null)
const historyListRef = ref<HTMLElement | null>(null)

// 悬浮提示相关状态
const STORAGE_KEY_TIP_CLOSED = 'ai_assistant_tip_closed'
const showFloatingTip = ref(localStorage.getItem(STORAGE_KEY_TIP_CLOSED) !== 'true')
const currentQuestionIndex = ref(0)
const tipTimer = ref<NodeJS.Timeout | null>(null)

let abortController: AbortController | null = null
let accumulatedContent = ''
let accumulatedThinking = ''
let currentMessage: Message | null = null
let animationFrameId: number | null = null

// 当前显示的问题
const currentQuestion = computed(() => {
	return presetQuestions.value[currentQuestionIndex.value] || ''
})

// 历史记录相关函数
const generateChatId = (): string => {
	return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
}

const generateChatTitle = (firstMessage: string): string => {
	const maxLength = 20
	const cleanMessage = firstMessage.replace(/[\n\r]/g, ' ').trim()
	return cleanMessage.length > maxLength ? cleanMessage.substring(0, maxLength) + '...' : cleanMessage
}

// 分页相关状态
const currentPage = ref(1)
const pageSize = ref(40)
const totalPages = ref(1)
const isLoadingMore = ref(false)
const hasMoreData = ref(true)

const loadChatHistories = async (page = 1, append = false) => {
	// 如果正在加载或没有更多数据，则不再加载
	if (isLoadingMore.value || (page > 1 && !hasMoreData.value)) {
		return
	}

	isLoadingMore.value = true
	// 从服务器加载历史记录
	apiRequest('post', '/ai/chat/record', {
		page: page,
		limit: pageSize.value
	})
		.then(res => {
			// 更新总页数
			totalPages.value = Math.ceil(res.data.total / pageSize.value)

			// 检查是否还有更多数据
			hasMoreData.value = page < totalPages.value

			// 将后端返回的数据格式转换为组件内部使用的格式
			const newHistories = res.data.list.map((item: any) => {
				// 创建一个基本的消息数组，包含AI的欢迎消息和用户的提问
				const messageArray: Message[] = [
					{
						sender: 'ai',
						content: t('componentsModule.aiAssistant.welcomeMessage'),
						timestamp: new Date(item.createTime).getTime()
					},
					{
						sender: 'user',
						content: item.prompt,
						timestamp: new Date(item.createTime).getTime() + 1000 // 用户消息时间戳略晚于AI欢迎消息
					}
				]

				// 如果有AI响应且状态正常，添加AI响应消息
				if (item.response && item.status === 0) {
					messageArray.push({
						sender: 'ai',
						content: item.response,
						timestamp: new Date(item.updateTime).getTime()
					})
				} else if (item.status === 1) {
					// 如果状态为错误，添加错误消息
					messageArray.push({
						sender: 'ai',
						content: `请求出错: ${item.errorMsg || '未知错误'}`,
						timestamp: new Date(item.updateTime).getTime()
					})
				}

				return {
					id: item.id,
					title: item.prompt,
					messages: messageArray,
					timestamp: new Date(item.createTime).getTime()
				}
			})

			// 如果是追加模式，则将新数据追加到现有数据后面
			if (append && page > 1) {
				chatHistories.value = [...chatHistories.value, ...newHistories]
			} else {
				chatHistories.value = newHistories
			}

			// 更新当前页码
			currentPage.value = page
		})
		.finally(() => {
			isLoadingMore.value = false
		})
}

const loadChatHistory = (history: ChatHistory) => {
	currentChatId.value = history.id
	messages.value = [...history.messages]
	showHistoryPanel.value = false
	nextTick(() => {
		scrollToBottom()
	})
}

// 处理历史记录列表滚动事件
const handleHistoryScroll = () => {
	if (!historyListRef.value) {
		return
	}

	const { scrollTop, scrollHeight, clientHeight } = historyListRef.value
	// 当滚动到底部时（考虑一点缓冲区，这里设为20px）
	if (scrollHeight - scrollTop - clientHeight < 20) {
		// 如果有更多数据且当前没有正在加载，则加载下一页
		if (hasMoreData.value && !isLoadingMore.value) {
			loadChatHistories(currentPage.value + 1, true)
		}
	}
}

const startNewChat = async () => {
	// 不再保存当前对话到服务器

	// 取消当前正在进行的请求
	if (abortController) {
		abortController.abort()
		abortController = null
	}

	// 重置isTyping状态
	isTyping.value = false
	accumulatedContent = ''
	currentMessage = null

	// 重置分页状态
	currentPage.value = 1
	hasMoreData.value = true

	// 重置到新对话
	await loadChatHistories(1, false)
	currentChatId.value = generateChatId()
	messages.value = [
		{
			sender: 'ai',
			content: t('componentsModule.aiAssistant.welcomeMessage'),
			timestamp: Date.now()
		}
	]
	showHistoryPanel.value = false
	nextTick(() => {
		scrollToBottom()
	})
}

const deleteChatHistory = async (chatId: string) => {
	// 只删除本地状态，不调用服务器接口
	chatHistories.value = chatHistories.value.filter(h => h.id !== chatId)

	// 如果删除的是当前对话，开始新对话
	if (currentChatId.value === chatId) {
		await startNewChat()
	}
}

const formatHistoryTime = (timestamp: number): string => {
	const date = new Date(timestamp)
	const now = new Date()
	const diffMs = now.getTime() - date.getTime()
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

	if (diffDays === 0) {
		return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
	} else if (diffDays === 1) {
		return t('componentsModule.aiAssistant.yesterday')
	} else if (diffDays < 7) {
		return t('componentsModule.aiAssistant.daysAgo', { days: diffDays })
	} else {
		return `${date.getMonth() + 1}/${date.getDate()}`
	}
}

const togglePresetQuestions = () => {
	showPresetQuestions.value = !showPresetQuestions.value
}

// 启动问题轮换定时器
const startQuestionRotation = () => {
	if (localStorage.getItem(STORAGE_KEY_TIP_CLOSED) === 'true') {
		return
	}

	if (tipTimer.value) {
		clearInterval(tipTimer.value)
	}
	tipTimer.value = setInterval(
		() => {
			currentQuestionIndex.value = (currentQuestionIndex.value + 1) % presetQuestions.value.length
			if (!isOpen.value && messages.value.length <= 1 && localStorage.getItem(STORAGE_KEY_TIP_CLOSED) !== 'true') {
				showFloatingTip.value = true
			}
		},
		10 * 60 * 1000
	) // 10分钟
}

// 停止问题轮换定时器
const stopQuestionRotation = () => {
	if (tipTimer.value) {
		clearInterval(tipTimer.value)
		tipTimer.value = null
	}
}

// 处理悬浮提示点击
const handleTipClick = () => {
	if (!isOpen.value) {
		toggleDialog()
	}
	handlePresetQuestion(currentQuestion.value)
	showFloatingTip.value = false
	stopQuestionRotation()
}

const handleCloseTip = () => {
	showFloatingTip.value = false
	localStorage.setItem(STORAGE_KEY_TIP_CLOSED, 'true')
	stopQuestionRotation()
}

// 拖拽开始
const startDrag = (e: MouseEvent) => {
	e.preventDefault()
	e.stopPropagation()
	isDragging.value = false

	mouseStart.value = { x: e.clientX, y: e.clientY }
	dragStart.value = { ...buttonPosition.value }
	document.addEventListener('mousemove', handleDrag, { passive: false })
	document.addEventListener('mouseup', endDrag, { passive: false })
	document.addEventListener('selectstart', preventSelect, { passive: false })

	document.body.style.userSelect = 'none'
	document.body.style.cursor = 'grabbing'
}

const preventSelect = (e: Event) => {
	e.preventDefault()
}

const handleDrag = (e: MouseEvent) => {
	e.preventDefault()

	const deltaX = e.clientX - mouseStart.value.x
	const deltaY = mouseStart.value.y - e.clientY

	if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
		isDragging.value = true
	}

	if (isDragging.value) {
		if (animationFrameId) {
			cancelAnimationFrame(animationFrameId)
		}
		animationFrameId = requestAnimationFrame(() => {
			const newX = Math.max(10, Math.min(window.innerWidth - 70, dragStart.value.x + deltaX))
			const newY = Math.max(10, Math.min(window.innerHeight - 70, dragStart.value.y + deltaY))

			buttonPosition.value = { x: newX, y: newY }
		})
	}
}

// 拖拽结束
const endDrag = () => {
	document.removeEventListener('mousemove', handleDrag)
	document.removeEventListener('mouseup', endDrag)
	document.removeEventListener('selectstart', preventSelect)
	document.body.style.userSelect = ''
	document.body.style.cursor = ''
	if (animationFrameId) {
		cancelAnimationFrame(animationFrameId)
		animationFrameId = null
	}
	setTimeout(() => {
		isDragging.value = false
	}, 50)
}

const handleButtonClick = () => {
	if (isDragging.value) {
		return
	}
	toggleDialog()
}

// 加载推荐问题
const loadRecommendedQuestions = async () => {
	isLoadingQuestions.value = true
	try {
		// 创建超时 Promise，3秒后超时
		const timeoutPromise = new Promise((_, reject) => {
			setTimeout(() => reject(new Error('Request timeout')), 3000)
		})

		const response = (await Promise.race([apiRequest('post', '/ai/chat/recommend'), timeoutPromise])) as any

		if (response && response.data) {
			// 假设接口返回的数据格式为 { data: ["问题1", "问题2", ...] }
			if (Array.isArray(response.data)) {
				presetQuestions.value = response.data
			} else if (response.data.list && Array.isArray(response.data.list)) {
				// 如果返回的是分页格式 { data: { list: [...] } }
				presetQuestions.value = response.data.list
			}
		}
	} catch (error) {
		console.warn('加载推荐问题失败或超时, 使用本地默认数据:', error)
		// 如果接口调用失败或超时，使用默认的推荐问题
		presetQuestions.value = [...DEFAULT_PRESET_QUESTIONS]
	} finally {
		isLoadingQuestions.value = false
	}
}

// 切换对话框显示状态
const toggleDialog = () => {
	isOpen.value = !isOpen.value
	if (isOpen.value) {
		showPresetQuestions.value = true
		showFloatingTip.value = false
		stopQuestionRotation()
		loadChatHistories(1, false)
		// 打开弹窗时加载推荐问题
		// loadRecommendedQuestions()
		nextTick(() => {
			scrollToBottom()
		})
	} else {
		setTimeout(() => {
			if (!isOpen.value && messages.value.length <= 1 && localStorage.getItem(STORAGE_KEY_TIP_CLOSED) !== 'true') {
				showFloatingTip.value = true
				startQuestionRotation()
			}
		}, 1000)
	}
}

// 发送消息
const sendMessage = async () => {
	const text = inputText.value.trim()
	if (!text || isTyping.value) {
		return
	}

	// 如果是新对话，生成ID
	if (!currentChatId.value) {
		currentChatId.value = generateChatId()
	}

	// 添加用户消息
	messages.value.push({
		sender: 'user',
		content: text,
		timestamp: Date.now()
	})

	const requestHistory = buildChatCompletionHistory(messages.value.slice(0, -1))

	inputText.value = ''
	isTyping.value = true

	// 滚动到底部
	await nextTick()
	scrollToBottom()

	try {
		// 取消之前的请求
		if (abortController) {
			abortController.abort()
		}

		// 创建新的中止控制器
		abortController = new AbortController()
		accumulatedContent = ''
		accumulatedThinking = ''

		// 创建新的AI消息
		currentMessage = {
			sender: 'ai',
			content: '',
			thinking: '',
			thinkingCollapsed: false,
			timestamp: Date.now()
		}
		messages.value.push(currentMessage)

		// 使用fetch进行流式请求，可以自定义请求头
		const apiUrl = `${window.location.origin}/api/ai/stream/completions`
		// const apiUrl2 = `${window.location.origin}/api/ai/stream/chat`
		const apiUrl2 = `http://1.92.106.14:8000/api/chat`
		// const apiUrl2 = `http://192.168.110.135:7860/api/v1/run/8c2c3c54-cb02-4619-9275-cffede3ae916?stream=true`
		const type = currentModel.value.type
		// const apiUrl = `http://192.168.10.118:7021/ai/stream/completions`
		console.log('Connecting to:', apiUrl)
		let response
		if (type === 'local') {
			response = await fetch(`${apiUrl}?prompt=${encodeURIComponent(text)}&stream=true&model=${currentModel.value.id}`, {
				method: 'GET',
				headers: {
					Accept: 'text/event-stream',
					Authorization: userStore.token, // 添加认证头
					'Cache-Control': 'no-cache'
				},
				signal: abortController.signal
			})
		} else {
			// response = await fetch(`${apiUrl2}`, {
			// 	method: 'POST',
			// 	headers: {
			// 		'Content-Type': 'application/json',
			// 		Accept: 'text/event-stream',
			// 		Authorization: userStore.token, // 添加认证头
			// 		'Cache-Control': 'no-cache'
			// 	},
			// 	body: JSON.stringify({
			// 		prompt: text,
			// 		model: currentModel.value.id,
			// 		stream: true
			// 	}),
			// 	signal: abortController.signal
			// })
			response = await fetch(`${apiUrl2}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'text/event-stream',
					Authorization: userStore.token, // 添加认证头
					'Cache-Control': 'no-cache',
					'x-api-key': 'sk-b6nRvZ32KWj5tC1ghuEzRqGnqGzBcUW--wlp_6xK_1k'
				},
				body: JSON.stringify({
					query: text,
					history: requestHistory,
					stream: true
					// input_value: '你好，请介绍一下自己'
				})
				// signal: abortController.signal
			})
		}

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		const contentType = response.headers.get('content-type') || ''
		const isEventStreamHeader = contentType.includes('text/event-stream')

		const reader = response.body?.getReader()
		if (!reader) {
			throw new Error('无法获取响应流')
		}

		const decoder = new TextDecoder('utf-8', { fatal: false, ignoreBOM: true })

		const syncMessageContentAndScroll = () => {
			const isAtBottom = chatContainer.value
				? chatContainer.value.scrollHeight - chatContainer.value.scrollTop - chatContainer.value.clientHeight < 50
				: true

			messages.value = [...messages.value]
			if (isAtBottom) {
				nextTick(() => {
					scrollToBottom()
				})
			}
		}

		const firstRead = await reader.read()
		if (firstRead.done) {
			isTyping.value = false
			return
		}

		const firstChunk = decoder.decode(firstRead.value, { stream: true })
		const looksLikeEventStream = isEventStreamHeader || /^\s*(data:|event:)\s*/m.test(firstChunk) || firstChunk.includes('[DONE]')

		if (!looksLikeEventStream) {
			if (firstChunk) {
				accumulatedContent += firstChunk
				if (currentMessage) {
					currentMessage.content = accumulatedContent
				}
				syncMessageContentAndScroll()
			}

			while (true) {
				const { done, value } = await reader.read()
				if (done) {
					isTyping.value = false
					break
				}

				const chunk = decoder.decode(value, { stream: true })
				if (!chunk) {
					continue
				}

				accumulatedContent += chunk
				if (currentMessage) {
					currentMessage.content = accumulatedContent
				}
				syncMessageContentAndScroll()
			}
			return
		}

		let buffer = firstChunk // 添加缓冲区来处理不完整的数据
		let chunkCount = 1 // 用于调试

		const processAvailableLinesFromBuffer = () => {
			const lines = splitBufferSafely(buffer)
			buffer = lines.incomplete || ''

			for (const line of lines.complete) {
				const trimmedLine = line.trim()
				if (!trimmedLine) {
					continue
				}
				processStreamLine(trimmedLine)
			}
		}

		processAvailableLinesFromBuffer()

		// 读取流数据
		while (true) {
			const { done, value } = await reader.read()

			if (done) {
				console.log('Stream completed, total chunks:', chunkCount)
				// 处理缓冲区中剩余的数据
				if (buffer.trim()) {
					console.log('Processing final buffer:', buffer.trim())
					processBufferedData(buffer.trim())
				}
				isTyping.value = false
				// AI回复完成后不再保存到服务器
				break
			}

			chunkCount++
			console.log(`Processing chunk ${chunkCount}, size:`, value.length)

			// 使用更安全的解码方式
			const chunk = decoder.decode(value, { stream: true })
			console.log('Decoded chunk:', chunk)

			buffer += chunk // 将新数据添加到缓冲区
			console.log('Current buffer length:', buffer.length)

			// 更智能的行分割：确保JSON完整性
			const lines = splitBufferSafely(buffer)
			buffer = lines.incomplete || '' // 保留不完整的部分

			for (const line of lines.complete) {
				const trimmedLine = line.trim()
				if (!trimmedLine) {
					continue
				}

				processStreamLine(trimmedLine)
			}
		}

		// 安全的缓冲区分割函数
		function splitBufferSafely(buffer: string): { complete: string[]; incomplete: string } {
			const lines = buffer.split('\n')
			const incomplete = lines.pop() || ''

			// 检查最后一个完整行是否是完整的JSON
			const complete = []
			for (let i = 0; i < lines.length; i++) {
				const line = lines[i].trim()
				if (!line) {
					continue
				}

				// 如果是JSON格式，检查是否完整
				if (line.startsWith('{') || line.startsWith('data: {')) {
					try {
						const jsonPart = line.startsWith('data: ') ? line.substring(6) : line
						JSON.parse(jsonPart) // 尝试解析以验证完整性
						complete.push(line)
					} catch (e) {
						// JSON不完整，放回缓冲区
						console.log('Incomplete JSON detected, keeping in buffer:', line)
						return { complete, incomplete: line + '\n' + incomplete }
					}
				} else {
					complete.push(line)
				}
			}

			return { complete, incomplete }
		}

		// 处理单行数据的函数
		function processStreamLine(trimmedLine: string) {
			console.log('=== Processing Stream Line ===')
			console.log('Raw line:', trimmedLine)

			let rawContent = trimmedLine

			// 处理Server-Sent Events的标准格式
			if (rawContent.startsWith('data: ')) {
				rawContent = rawContent.substring(6)
				console.log('Extracted SSE data:', rawContent)
			}

			// 检查结束标志
			if (
				rawContent === '[DONE]' ||
				rawContent === 'data: [DONE]' ||
				rawContent === 'event: close' ||
				rawContent === 'event:close' ||
				rawContent === '流式传输结束'
			) {
				console.log('Stream completed----------------------------------------')
				isTyping.value = false
				return
			}

			// 跳过空消息、心跳包和结束提示文本
			if (
				!rawContent ||
				rawContent === 'data:' ||
				rawContent === ':heartbeat' ||
				rawContent === '流式传输结束' ||
				rawContent === 'Stream ended' ||
				rawContent === 'stream completed' ||
				rawContent === '传输完成' ||
				rawContent.includes('流式传输结束') ||
				rawContent.includes('Stream ended')
			) {
				console.log('Skipping empty, heartbeat or end message:', rawContent)
				return
			}

			let content = ''
			let isThinking = false

			// 尝试多种解析方式
			if (rawContent.startsWith('{') || rawContent.startsWith('[')) {
				// JSON格式处理
				try {
					// 使用自定义JSON解析，保护数字精度
					const parsed = parseJSONSafely(rawContent)
					console.log('Safely parsed data:', parsed)

					if (parsed.type === 'thinking') {
						content = parsed.content
						isThinking = true
						console.log('✓ Extracted from thinking:', content)
					} else if (parsed.type === 'answer') {
						content = parsed.content
						isThinking = false
						console.log('✓ Extracted from answer:', content)
					} else if (parsed.choices && parsed.choices[0] && parsed.choices[0].delta) {
						if (parsed.choices[0].delta.content !== undefined) {
							content = parsed.choices[0].delta.content
							console.log('✓ Extracted from choices.delta.content:', content)
						}
					} else if (parsed.content !== undefined) {
						content = parsed.content
						console.log('✓ Extracted from content:', content)
					} else if (parsed.data !== undefined) {
						content = parsed.data
						console.log('✓ Extracted from data:', content)
					} else if (parsed.message !== undefined) {
						content = parsed.message
						console.log('✓ Extracted from message:', content)
					} else if (parsed.text !== undefined) {
						content = parsed.text
						console.log('✓ Extracted from text:', content)
					} else {
						console.log('⚠ No content found in parsed JSON:', parsed)
					}
				} catch (jsonError) {
					const fixedContent = attemptJSONFix(rawContent)
					if (fixedContent) {
						content = fixedContent
						console.log('✓ Fixed and extracted content:', content)
					} else {
						console.warn('❌ Skipping corrupted JSON:', rawContent)
						return
					}
				}
			} else {
				// 纯文本内容
				content = rawContent
				console.log('✓ Using raw text content:', content)
			}

			// 更新消息内容
			if (content !== undefined && content !== null && currentMessage) {
				console.log('=== Content Processing ===')
				console.log('Original content:', content)
				console.log('Content type:', typeof content)
				console.log('Content length:', String(content).length)

				// 检查是否包含数字
				const contentStr = String(content)
				const hasNumbers = /\d/.test(contentStr)
				if (hasNumbers) {
					const numbers = contentStr.match(/\d+(\.\d+)?/g)
					console.log('🔢 Content contains numbers:', numbers)
				}

				// 使用最安全的字符串转换
				const safeContent = preserveNumbers(content)
				console.log('✓ Safe content:', safeContent)

				if (isThinking) {
					accumulatedThinking += safeContent
					currentMessage.thinking = accumulatedThinking
				} else {
					accumulatedContent += safeContent
					currentMessage.content = accumulatedContent
				}

				// 检查当前是否在底部 (阈值50px)
				const isAtBottom = chatContainer.value
					? chatContainer.value.scrollHeight - chatContainer.value.scrollTop - chatContainer.value.clientHeight < 50
					: true

				messages.value = [...messages.value]
				// 只有当用户原本就在底部时，才自动滚动到底部
				if (isAtBottom) {
					nextTick(() => {
						scrollToBottom()
					})
				}
			} else if (content === '') {
				console.log('⚠ Empty content, but still valid')
				// 空字符串也是有效内容，可能是换行或空格
				if (currentMessage) {
					if (isThinking) {
						accumulatedThinking += content
						currentMessage.thinking = accumulatedThinking
					} else {
						accumulatedContent += content
						currentMessage.content = accumulatedContent
					}
					messages.value = [...messages.value]
				}
			} else {
				console.log('❌ Invalid content, skipping:', content)
			}
		}

		// 安全的JSON解析函数
		function parseJSONSafely(jsonString: string): any {
			// 预处理：修复常见的JSON问题
			let cleanJson = jsonString.trim()

			// 移除可能的BOM
			if (cleanJson.charCodeAt(0) === 0xfeff) {
				cleanJson = cleanJson.slice(1)
			}

			return JSON.parse(cleanJson)
		}

		// 尝试修复损坏的JSON
		function attemptJSONFix(brokenJson: string): string | null {
			try {
				// 尝试提取引号内的内容
				const contentMatch = brokenJson.match(/"content"\s*:\s*"([^"]*)"/)
				if (contentMatch) {
					return contentMatch[1]
				}

				// 尝试提取其他可能的内容字段
				const dataMatch = brokenJson.match(/"data"\s*:\s*"([^"]*)"/)
				if (dataMatch) {
					return dataMatch[1]
				}

				return null
			} catch (e) {
				return null
			}
		}

		// 保护数字的字符串转换
		function preserveNumbers(value: any): string {
			if (typeof value === 'number') {
				// 对于数字，使用最精确的字符串表示
				if (Number.isInteger(value) && Math.abs(value) > Number.MAX_SAFE_INTEGER) {
					console.warn('Large integer detected, may lose precision:', value)
				}
				return value.toString()
			} else if (typeof value === 'string') {
				return value
			} else if (value === null || value === undefined) {
				return ''
			} else {
				// 对于其他类型，使用JSON序列化
				return JSON.stringify(value)
			}
		}

		// 处理缓冲区数据的函数
		function processBufferedData(data: string) {
			processStreamLine(data)
		}
	} catch (error: any) {
		if (error.name === 'AbortError') {
			console.log('请求已取消')
			return
		}

		console.error('连接错误:', error)
		ElMessage.error(t('componentsModule.aiAssistant.connectionError'))
		isTyping.value = false

		if (currentMessage) {
			currentMessage.content = t('componentsModule.aiAssistant.connectionErrorMessage')
		}
	}
}

// 处理键盘事件
const handleKeyDown = (event: KeyboardEvent) => {
	if (event.key === 'Enter' && !event.shiftKey) {
		sendMessage()
	}
}

// 处理预设问题点击
const handlePresetQuestion = (question: string) => {
	if (isTyping.value) {
		return
	}
	// 隐藏悬浮提示
	showFloatingTip.value = false
	stopQuestionRotation()
	inputText.value = question
	sendMessage()
}

// 重置对话
const resetChat = async () => {
	// 取消当前请求
	if (abortController) {
		abortController.abort()
		abortController = null
	}

	// 重置状态
	isTyping.value = false
	inputText.value = ''
	accumulatedContent = ''
	currentMessage = null

	// 调用新建对话功能
	await startNewChat()
}

// 滚动到底部
const scrollToBottom = () => {
	if (chatContainer.value) {
		chatContainer.value.scrollTop = chatContainer.value.scrollHeight
	}
}

// 切换思考内容显示
const toggleThinking = (message: Message) => {
	message.thinkingCollapsed = !message.thinkingCollapsed
}

// 格式化消息内容
const formatMessage = (text: string): string => {
	if (!text) {
		return ''
	}

	let result = text
	const brPlaceholder = '___BR_PLACEHOLDER___'
	result = result.replace(/<br\s*\/?>/gi, brPlaceholder)
	const codeBlocks: string[] = []
	const codeBlockPlaceholder = '___CODE_BLOCK_PLACEHOLDER___'
	const inlineCodes: string[] = []
	const inlineCodePlaceholder = '___INLINE_CODE_PLACEHOLDER___'

	result = result.replace(/```(\w+)?\n?([\s\S]*?)```/g, (match, lang, code) => {
		const language = lang || 'text'
		const processedCode = `<pre class="ai-code-block"><code class="ai-code-block-content language-${language}">${code.trim()}</code></pre>`
		codeBlocks.push(processedCode)
		return codeBlockPlaceholder + (codeBlocks.length - 1) + codeBlockPlaceholder
	})

	result = result.replace(/`([^`\n]+)`/g, (match, code) => {
		const processedCode = `<code class="ai-code">${code}</code>`
		inlineCodes.push(processedCode)
		return inlineCodePlaceholder + (inlineCodes.length - 1) + inlineCodePlaceholder
	})

	result = result.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')

	result = result.replace(/^#### (.*$)/gm, '<h4 class="ai-heading ai-heading-4">$1</h4>')
	result = result.replace(/^### (.*$)/gm, '<h3 class="ai-heading ai-heading-3">$1</h3>')
	result = result.replace(/^## (.*$)/gm, '<h2 class="ai-heading ai-heading-2">$1</h2>')
	result = result.replace(/^# (.*$)/gm, '<h1 class="ai-heading ai-heading-1">$1</h1>')

	result = result.replace(/\*\*\*(.*?)\*\*\*/g, '<strong class="ai-bold"><em class="ai-italic">$1</em></strong>')
	result = result.replace(/\*\*(.*?)\*\*/g, '<strong class="ai-bold">$1</strong>')
	result = result.replace(/\*(.*?)\*/g, '<em class="ai-italic">$1</em>')

	result = result.replace(/~~(.*?)~~/g, '<del class="ai-strikethrough">$1</del>')

	result = result.replace(/^(\s*)[-*+] (.*$)/gm, (match, indent, content) => {
		const level = Math.floor(indent.length / 2) || 0
		return `<li class="ai-list-item" data-level="${level}">${content}</li>`
	})

	result = result.replace(/^(\s*)\d+\. (.*$)/gm, (match, indent, content) => {
		const level = Math.floor(indent.length / 2) || 0
		return `<li class="ai-list-item-ordered" data-level="${level}">${content}</li>`
	})

	result = result.replace(/(<li class="ai-list-item"[^>]*>.*?<\/li>(\s*<li class="ai-list-item"[^>]*>.*?<\/li>)*)/gs, '<ul class="ai-list">$1</ul>')
	result = result.replace(
		/(<li class="ai-list-item-ordered"[^>]*>.*?<\/li>(\s*<li class="ai-list-item-ordered"[^>]*>.*?<\/li>)*)/gs,
		'<ol class="ai-list-ordered">$1</ol>'
	)

	result = result.replace(/^> (.*$)/gm, '<blockquote class="ai-blockquote">$1</blockquote>')

	result = result.replace(/(\|.*\|)\n(\|[-:\s]*\|)\n((\|.*\|\n?)*)/g, (match: string, header: string, separator: string, rows: string) => {
		const headerCells = header
			.split('|')
			.slice(1, -1)
			.map((cell: string) => `<th class="ai-table-header">${cell.trim()}</th>`)
			.join('')
		const rowsHtml = rows
			.trim()
			.split('\n')
			.map((row: string) => {
				const cells = row
					.split('|')
					.slice(1, -1)
					.map((cell: string) => `<td class="ai-table-cell">${cell.trim()}</td>`)
					.join('')
				return `<tr class="ai-table-row">${cells}</tr>`
			})
			.join('')
		return `<table class="ai-table"><thead><tr class="ai-table-header-row">${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table>`
	})

	result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="ai-link">$1</a>')

	result = result.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" class="ai-link">$1</a>')

	result = result.replace(/^---+$/gm, '<hr class="ai-divider">')

	result = result.replace(/\n/g, '<br>')

	result = result.replace(new RegExp(codeBlockPlaceholder + '(\\d+)' + codeBlockPlaceholder, 'g'), (match, index) => {
		return codeBlocks[parseInt(index)]
	})

	result = result.replace(new RegExp(inlineCodePlaceholder + '(\\d+)' + inlineCodePlaceholder, 'g'), (match, index) => {
		return inlineCodes[parseInt(index)]
	})

	result = result.replace(new RegExp(brPlaceholder, 'g'), '<br>')

	return result
}

// 格式化时间
const formatTime = (timestamp: number): string => {
	const date = new Date(timestamp)
	return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
}

// 处理全局点击事件
const handleGlobalClick = (event: Event) => {
	const target = event.target as HTMLElement

	// 处理模型选择器点击外部关闭
	if (showModelSelector.value) {
		const modelSelector = document.querySelector('.ai-model-selector')
		if (modelSelector && !modelSelector.contains(target)) {
			showModelSelector.value = false
		}
	}

	if (!showFloatingTip.value) {
		return
	}
	const floatingTipEl = floatingTipRef.value
	const floatingButtonEl = floatingButtonRef.value
	// 如果点击的不是悬浮提示框或浮动按钮，则隐藏悬浮提示
	if (floatingTipEl && floatingButtonEl && !floatingTipEl.contains(target) && !floatingButtonEl.contains(target)) {
		showFloatingTip.value = false
	}
}

// 组件挂载时启动定时器
onMounted(async () => {
	// 加载历史记录
	await loadChatHistories(1, false)

	// 初始化当前对话ID
	if (!currentChatId.value) {
		currentChatId.value = generateChatId()
	}

	if (messages.value.length <= 1) {
		startQuestionRotation()
	}
	document.addEventListener('click', handleGlobalClick)
})

// 组件卸载时清理
onBeforeUnmount(() => {
	if (abortController) {
		abortController.abort()
	}

	stopQuestionRotation()

	document.removeEventListener('mousemove', handleDrag)
	document.removeEventListener('mouseup', endDrag)
	document.removeEventListener('selectstart', preventSelect)
	document.removeEventListener('click', handleGlobalClick)

	if (animationFrameId) {
		cancelAnimationFrame(animationFrameId)
	}
	document.body.style.userSelect = ''
	document.body.style.cursor = ''
})
</script>

<style lang="scss" scoped>
.ai-assistant {
	position: fixed;
	z-index: 9999;
	left: 20px;
	bottom: 20px;
}

.ai-float-button {
	position: fixed;
	width: 60px;
	height: 60px;
	background: linear-gradient(135deg, #209cee 0%, #4eccff 50%, #2ed573 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: grab;
	box-shadow:
		0 0 20px rgba(32, 156, 238, 0.4),
		0 0 40px rgba(78, 204, 255, 0.2),
		inset 0 1px 0 rgba(255, 255, 255, 0.2);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
	border: 2px solid rgba(78, 204, 255, 0.3);
	backdrop-filter: blur(10px);
	user-select: none;
	will-change: transform;

	&::before {
		content: '';
		position: absolute;
		top: -2px;
		left: -2px;
		right: -2px;
		bottom: -2px;
		border-radius: 50%;
		background: linear-gradient(45deg, #209cee, #4eccff, #2ed573);
		opacity: 0;
		transition: opacity 0.3s ease;
		z-index: -1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		width: 8px;
		height: 8px;
		background: #2ed573;
		border-radius: 50%;
		transform: translate(-50%, -50%);
		animation: pulse-ring 2s infinite;
	}

	&:hover {
		transform: scale(1.05);
		box-shadow:
			0 0 30px rgba(32, 156, 238, 0.6),
			0 0 60px rgba(78, 204, 255, 0.3),
			inset 0 1px 0 rgba(255, 255, 255, 0.3);

		&::before {
			opacity: 0.8;
		}
	}

	&:active {
		cursor: grabbing;
		transform: scale(0.98);
		transition: none;
	}

	&--active {
		background: linear-gradient(135deg, #ff4757 0%, #ff6b7a 50%, #ff8a9b 100%);
		box-shadow:
			0 0 20px rgba(255, 71, 87, 0.4),
			0 0 40px rgba(255, 107, 122, 0.2);
		animation: pulse-active 1.5s infinite;

		&::before {
			background: linear-gradient(45deg, #ff4757, #ff6b7a, #ff8a9b);
		}

		&:hover {
			transform: scale(1.05);
			box-shadow:
				0 0 30px rgba(255, 71, 87, 0.6),
				0 0 60px rgba(255, 107, 122, 0.3);
		}
	}

	.ai-icon {
		color: white;
		filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
		z-index: 1;
		pointer-events: none;
	}
}

.ai-floating-tip {
	position: fixed;
	z-index: 9998;
	max-width: 320px;
	animation: fadeInSlide 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
	cursor: pointer;
	transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	filter: drop-shadow(0 12px 40px rgba(0, 0, 0, 0.15));

	&:hover {
		transform: translateY(-3px) scale(1.02);
		filter: drop-shadow(0 16px 50px rgba(0, 0, 0, 0.2)) brightness(1.05);
	}
}

.ai-floating-tip__content {
	background: linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 50%, rgba(241, 245, 249, 0.95) 100%);
	border: 1px solid rgba(226, 232, 240, 0.8);
	border-radius: 16px;
	padding: 16px 20px;
	box-shadow:
		0 20px 60px rgba(0, 0, 0, 0.08),
		0 8px 25px rgba(0, 0, 0, 0.06),
		inset 0 1px 0 rgba(255, 255, 255, 0.9);
	backdrop-filter: blur(20px) saturate(180%);
	display: flex;
	align-items: flex-start;
	gap: 12px;
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%);
		border-radius: 16px 16px 0 0;
		opacity: 0.9;
		animation: gradientShift 3s ease-in-out infinite;
	}

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
			radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%);
		border-radius: 16px;
		pointer-events: none;
	}
}

.ai-floating-tip__icon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow:
		0 4px 12px rgba(102, 126, 234, 0.3),
		0 2px 6px rgba(102, 126, 234, 0.2),
		inset 0 1px 0 rgba(255, 255, 255, 0.3);
	position: relative;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		top: -50%;
		left: -50%;
		width: 200%;
		height: 200%;
		background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
		transform: rotate(-45deg);
		animation: shimmer 2s infinite;
	}

	.el-icon {
		color: white;
		filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
		z-index: 1;
	}
}

.ai-floating-tip__text {
	flex: 1;
	color: #334155;
	font-size: 14px;
	line-height: 1.5;
	margin: 2px 0;
	word-break: break-word;
	font-weight: 500;
	text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

.ai-floating-tip__close {
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background: rgba(148, 163, 184, 0.1);
	border: 1px solid rgba(203, 213, 225, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	flex-shrink: 0;
	margin-top: -1px;
	backdrop-filter: blur(10px);

	&:hover {
		background: rgba(239, 68, 68, 0.1);
		border-color: rgba(239, 68, 68, 0.3);
		transform: scale(1.1) rotate(90deg);
		box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
	}

	.el-icon {
		color: #64748b;
		transition: all 0.3s ease;
	}

	&:hover .el-icon {
		color: #ef4444;
	}
}

.ai-floating-tip__arrow {
	position: absolute;
	left: -10px;
	top: 50%;
	transform: translateY(-50%);
	width: 0;
	height: 0;
	border-top: 10px solid transparent;
	border-bottom: 10px solid transparent;
	border-right: 10px solid rgba(248, 250, 252, 0.98);
	filter: drop-shadow(-2px 0 4px rgba(0, 0, 0, 0.06));

	&::before {
		content: '';
		position: absolute;
		left: 1px;
		top: -9px;
		width: 0;
		height: 0;
		border-top: 9px solid transparent;
		border-bottom: 9px solid transparent;
		border-right: 9px solid rgba(226, 232, 240, 0.8);
	}
}

.ai-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(8, 17, 32, 0.45);
	backdrop-filter: blur(2px);
	z-index: -1;
}

.ai-dialog {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%) scale(0.8);
	width: 1200px;
	height: 700px;
	background: transparent;
	border-radius: 20px;
	box-shadow:
		0 20px 60px rgba(0, 0, 0, 0.3),
		0 0 40px rgba(32, 156, 238, 0.1),
		inset 0 1px 0 rgba(78, 204, 255, 0.2);
	display: flex;
	flex-direction: row;
	opacity: 0;
	transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	border: 1px solid rgba(32, 156, 238, 0.3);
	backdrop-filter: blur(20px);
	overflow: hidden;
	z-index: 1000;
	&--show {
		transform: translate(-50%, -50%) scale(1);
		opacity: 1;
	}
}

.ai-dialog__main {
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	background: linear-gradient(145deg, rgba(13, 28, 50, 0.95) 0%, rgba(26, 41, 66, 0.95) 100%);
	border-radius: 0 20px 20px 0;
}

.ai-dialog__header {
	padding: 8px 16px;
	// border-bottom: 1px solid rgba(32, 156, 238, 0.2);
	background: linear-gradient(135deg, rgba(32, 156, 238, 0.15) 0%, rgba(78, 204, 255, 0.1) 100%);
	border-radius: 0 20px 0 0;
	color: var(--deep-blue-text-primary, #e0f7ff);
	position: relative;
	display: flex;
	flex-direction: column;
}

.ai-dialog__title {
	display: flex;
	align-items: center;
	gap: 12px;
	font-weight: 600;
	font-size: 18px;
	color: #4eccff;
	text-shadow: 0 0 10px rgba(78, 204, 255, 0.3);
	animation: glow 3s ease-in-out infinite;

	.ai-title-icon {
		color: #4eccff;
		filter: drop-shadow(0 0 5px rgba(78, 204, 255, 0.5));
	}
}

.ai-dialog__subtitle {
	font-size: 13px;
	opacity: 0.7;
	margin-top: 6px;
	color: var(--deep-blue-text-secondary, rgba(224, 247, 255, 0.7));
	letter-spacing: 0.5px;
}

.ai-dialog__actions {
	position: absolute;
	top: 20px;
	right: 24px;
	display: flex;
	align-items: center;
	gap: 8px;
}

.ai-dialog__reset {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(78, 204, 255, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	backdrop-filter: blur(10px);

	&:hover:not(.ai-dialog__reset--disabled) {
		background: rgba(46, 213, 115, 0.2);
		border-color: rgba(46, 213, 115, 0.5);
		transform: scale(1.1);
		box-shadow: 0 4px 15px rgba(46, 213, 115, 0.2);
	}

	&--disabled {
		opacity: 0.5;
		cursor: not-allowed;
		transform: none !important;
	}

	.el-icon {
		color: rgba(224, 247, 255, 0.8);
		transition: color 0.3s ease;
	}

	&:hover:not(.ai-dialog__reset--disabled) .el-icon {
		color: #2ed573;
	}
}

.ai-dialog__close {
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(78, 204, 255, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	backdrop-filter: blur(10px);

	&:hover {
		background: rgba(255, 71, 87, 0.2);
		border-color: rgba(255, 71, 87, 0.5);
		transform: scale(1.1);
		box-shadow: 0 4px 15px rgba(255, 71, 87, 0.2);
	}

	.el-icon {
		color: rgba(224, 247, 255, 0.8);
		transition: color 0.3s ease;
	}

	&:hover .el-icon {
		color: #ff4757;
	}
}

.ai-dialog__content {
	flex: 1;
	padding: 24px;
	overflow-y: auto;
	background: transparent;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: rgba(13, 28, 50, 0.3);
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb {
		background: linear-gradient(180deg, #209cee, #4eccff);
		border-radius: 3px;
		box-shadow: 0 0 5px rgba(78, 204, 255, 0.3);

		&:hover {
			background: linear-gradient(180deg, #4eccff, #209cee);
		}
	}
}

.ai-message {
	display: flex;
	margin-bottom: 20px;
	animation: fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);

	&--user {
		flex-direction: row-reverse;

		.ai-message__content {
			background: linear-gradient(135deg, rgba(78, 204, 255, 0.2) 0%, rgba(32, 156, 238, 0.3) 100%);
			color: var(--deep-blue-text-primary, #e0f7ff);
			border-radius: 20px 20px 6px 20px;
			margin-right: 12px;
			border: 1px solid rgba(78, 204, 255, 0.3);
			box-shadow: 0 4px 15px rgba(78, 204, 255, 0.1);
		}

		.ai-message__avatar {
			background: linear-gradient(135deg, #4eccff 0%, #209cee 100%);
			border: 2px solid rgba(78, 204, 255, 0.4);
			color: white;
		}
	}

	&--ai {
		.ai-message__content {
			background: linear-gradient(135deg, rgba(46, 213, 115, 0.1) 0%, rgba(46, 213, 115, 0.05) 100%);
			color: var(--deep-blue-text-primary, #e0f7ff);
			border-radius: 20px 20px 20px 6px;
			margin-left: 12px;
			border: 1px solid rgba(46, 213, 115, 0.2);
			box-shadow: 0 4px 15px rgba(46, 213, 115, 0.05);
		}

		.ai-message__avatar {
			background: linear-gradient(135deg, #2ed573 0%, #20bf6b 100%);
			border: 2px solid rgba(46, 213, 115, 0.4);
			color: white;
		}
	}
}

.ai-message__avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
	backdrop-filter: blur(10px);
}

.ai-message__content {
	max-width: 80%;
	padding: 16px 20px;
	backdrop-filter: blur(10px);
	position: relative;
	min-width: 200px;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border-radius: inherit;
		background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.05), transparent);
		opacity: 0.5;
		z-index: -1;
	}
}

.ai-message__text {
	line-height: 1.6;
	word-break: break-word;
	font-size: 14px;
}

.ai-message__thinking {
	margin-bottom: 12px;
	padding: 12px;
	background: rgba(46, 213, 115, 0.05);
	border-left: 3px solid #2ed573;
	border-radius: 4px;
	font-size: 13px;
	color: rgba(224, 247, 255, 0.8);

	.ai-message__thinking-label {
		font-weight: 600;
		color: #2ed573;
		margin-bottom: 6px;
		font-size: 12px;
		display: flex;
		align-items: center;
		cursor: pointer;
		user-select: none;
		width: fit-content;
		transition: opacity 0.2s;

		&:hover {
			opacity: 0.8;
		}

		&::before {
			content: '';
			display: inline-block;
			width: 6px;
			height: 6px;
			background: #2ed573;
			border-radius: 50%;
			margin-right: 8px;
			animation: pulse 2s infinite;
		}

		.ai-thinking-icon {
			margin-right: 4px;
			font-size: 12px;
			transition: transform 0.3s ease;

			&.is-collapsed {
				transform: rotate(-90deg);
			}
		}
	}

	.ai-message__thinking-content {
		line-height: 1.5;
		font-style: italic;
		white-space: pre-wrap;
	}
}

.ai-message__time {
	font-size: 11px;
	opacity: 0.6;
	margin-top: 6px;
	color: var(--deep-blue-text-secondary, rgba(224, 247, 255, 0.7));
}

.ai-typing-indicator {
	display: flex;
	align-items: center;
	gap: 6px;

	span {
		width: 8px;
		height: 8px;
		background: linear-gradient(45deg, #2ed573, #4eccff);
		border-radius: 50%;
		animation: typing-bounce 1.4s infinite ease-in-out both;
		box-shadow: 0 0 8px rgba(46, 213, 115, 0.5);

		&:nth-child(1) {
			animation-delay: -0.32s;
		}
		&:nth-child(2) {
			animation-delay: -0.16s;
		}
		&:nth-child(3) {
			animation-delay: 0s;
		}
	}
}

.ai-dialog__footer {
	padding: 20px 24px;
	border-top: 1px solid rgba(32, 156, 238, 0.2);
	background: linear-gradient(135deg, rgba(13, 28, 50, 0.8) 0%, rgba(26, 41, 66, 0.6) 100%);
	border-radius: 0 0 20px 0;
	backdrop-filter: blur(20px);
}

.ai-input-wrapper {
	display: flex;
	gap: 12px;
	align-items: flex-end;
}

.ai-send-btn {
	height: 44px;
	width: 44px;
	border-radius: 12px;
	background: linear-gradient(135deg, #209cee 0%, #4eccff 100%);
	border: 1px solid rgba(78, 204, 255, 0.4);
	box-shadow: 0 4px 15px rgba(32, 156, 238, 0.3);
	transition: all 0.3s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 6px 20px rgba(32, 156, 238, 0.4);
		background: linear-gradient(135deg, #4eccff 0%, #209cee 100%);
	}

	&:active {
		transform: translateY(0);
	}
}

.ai-model-selector {
	position: relative;
	margin-top: 6px;
	width: fit-content;
}

.ai-model-current {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 12px;
	background: rgba(32, 156, 238, 0.1);
	border: 1px solid rgba(78, 204, 255, 0.2);
	border-radius: 20px;
	cursor: pointer;
	transition: all 0.3s ease;
	user-select: none;

	&:hover {
		background: rgba(32, 156, 238, 0.2);
		border-color: rgba(78, 204, 255, 0.4);
	}

	.ai-model-type-icon {
		display: flex;
		align-items: center;
		color: #4eccff;
	}

	.ai-model-name {
		font-size: 13px;
		color: #e0f7ff;
		font-weight: 500;
	}

	.ai-model-arrow {
		color: #4eccff;
		font-size: 12px;
		transition: transform 0.3s ease;

		&.is-rotated {
			transform: rotate(180deg);
		}
	}
}

.ai-model-dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	margin-top: 8px;
	width: 320px;
	background: rgba(13, 28, 50, 0.95);
	border: 1px solid rgba(78, 204, 255, 0.3);
	border-radius: 12px;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
	backdrop-filter: blur(20px);
	z-index: 2000;
	padding: 8px;
	overflow: hidden;
}

.ai-model-dropdown-header {
	font-size: 12px;
	color: rgba(224, 247, 255, 0.5);
	padding: 8px 12px;
	margin-bottom: 4px;
}

.ai-model-option {
	padding: 12px;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	border: 1px solid transparent;
	margin-bottom: 4px;

	&:last-child {
		margin-bottom: 0;
	}

	&:hover {
		background: rgba(78, 204, 255, 0.1);
	}

	&.is-selected {
		background: rgba(32, 156, 238, 0.15);
		border-color: rgba(78, 204, 255, 0.3);

		.ai-model-option-name {
			color: #4eccff;
		}
	}
}

.ai-model-option-top {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 4px;
}

.ai-model-option-name {
	font-size: 14px;
	font-weight: 600;
	color: #e0f7ff;
}

.ai-model-check {
	color: #2ed573;
}

.ai-model-desc {
	font-size: 12px;
	color: rgba(224, 247, 255, 0.6);
	margin-bottom: 8px;
	line-height: 1.4;
}

.ai-model-skills {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.ai-skill-tag {
	font-size: 10px;
	padding: 2px 8px;
	background: rgba(78, 204, 255, 0.1);
	border-radius: 10px;
	color: #4eccff;
	border: 1px solid rgba(78, 204, 255, 0.2);
}

:deep(.el-textarea__inner) {
	background: rgba(13, 28, 50, 0.4) !important;
	border: 1px solid rgba(32, 156, 238, 0.3) !important;
	border-radius: 12px !important;
	color: var(--deep-blue-text-primary, #e0f7ff) !important;
	resize: none;
	padding: 12px 16px !important;
	box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2) !important;
	backdrop-filter: blur(10px);
	transition: all 0.3s ease !important;

	&::placeholder {
		color: rgba(224, 247, 255, 0.5) !important;
	}

	&:focus {
		border-color: rgba(78, 204, 255, 0.6) !important;
		box-shadow:
			inset 0 2px 8px rgba(0, 0, 0, 0.2),
			0 0 15px rgba(78, 204, 255, 0.2) !important;
	}
}

:deep(.el-textarea .el-input__count) {
	background: rgba(13, 28, 50, 0.8);
	color: rgba(224, 247, 255, 0.6);
	border-radius: 6px;
	padding: 2px 6px;
	margin: 4px;
}

// Markdown样式
.ai-code {
	background: rgba(13, 28, 50, 0.6);
	color: #4eccff;
	padding: 4px 8px;
	border-radius: 6px;
	font-family: 'Fira Code', 'Courier New', monospace;
	font-size: 0.9em;
	border: 1px solid rgba(78, 204, 255, 0.2);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.ai-code-block {
	background: rgba(13, 28, 50, 0.8);
	border: 1px solid rgba(78, 204, 255, 0.3);
	border-radius: 12px;
	margin: 12px 0;
	overflow-x: auto;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

	&::-webkit-scrollbar {
		height: 6px;
	}

	&::-webkit-scrollbar-track {
		background: rgba(13, 28, 50, 0.3);
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb {
		background: linear-gradient(90deg, #209cee, #4eccff);
		border-radius: 3px;
		box-shadow: 0 0 5px rgba(78, 204, 255, 0.3);

		&:hover {
			background: linear-gradient(90deg, #4eccff, #209cee);
		}
	}
}

.ai-code-block-content {
	display: block;
	padding: 16px 20px;
	color: #e0f7ff;
	font-family: 'Fira Code', 'Courier New', monospace;
	font-size: 14px;
	line-height: 1.5;
	white-space: pre;
	margin: 0;
}

.ai-heading {
	margin: 16px 0 12px 0;
	font-weight: 600;
	color: #4eccff;
	text-shadow: 0 0 10px rgba(78, 204, 255, 0.3);

	&.ai-heading-1 {
		font-size: 1.8em;
		border-bottom: 2px solid rgba(78, 204, 255, 0.3);
		padding-bottom: 8px;
	}

	&.ai-heading-2 {
		font-size: 1.5em;
		border-bottom: 1px solid rgba(78, 204, 255, 0.2);
		padding-bottom: 6px;
	}

	&.ai-heading-3 {
		font-size: 1.3em;
	}

	&.ai-heading-4 {
		font-size: 1.1em;
	}
}

.ai-bold {
	font-weight: 600;
	color: #4eccff;
}

.ai-italic {
	font-style: italic;
	color: #2ed573;
}

.ai-strikethrough {
	text-decoration: line-through;
	opacity: 0.7;
}

.ai-list {
	margin: 12px 0;
	padding-left: 20px;
	list-style: none;

	.ai-list-item {
		position: relative;
		margin: 6px 0;
		line-height: 1.6;

		&::before {
			content: '•';
			color: #4eccff;
			font-size: 1.2em;
			position: absolute;
			left: -16px;
			top: 0;
		}
	}
}

.ai-list-ordered {
	margin: 12px 0;
	padding-left: 20px;
	counter-reset: list-counter;

	.ai-list-item-ordered {
		position: relative;
		margin: 6px 0;
		line-height: 1.6;
		counter-increment: list-counter;

		&::before {
			content: counter(list-counter) '.';
			color: #4eccff;
			font-weight: 600;
			position: absolute;
			left: -20px;
			top: 0;
		}
	}
}

.ai-blockquote {
	background: rgba(46, 213, 115, 0.1);
	border-left: 4px solid #2ed573;
	margin: 12px 0;
	padding: 12px 16px;
	border-radius: 0 8px 8px 0;
	font-style: italic;
	color: rgba(224, 247, 255, 0.9);
	box-shadow: 0 2px 8px rgba(46, 213, 115, 0.1);
}

.ai-link {
	color: #4eccff;
	text-decoration: underline;
	text-decoration-color: rgba(78, 204, 255, 0.5);
	transition: all 0.3s ease;

	&:hover {
		color: #2ed573;
		text-decoration-color: rgba(46, 213, 115, 0.8);
		text-shadow: 0 0 8px rgba(46, 213, 115, 0.3);
	}
}

.ai-table {
	width: 100%;
	border-collapse: collapse;
	margin: 12px 0;
	background: rgba(13, 28, 50, 0.4);
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

	.ai-table-header {
		background: rgba(78, 204, 255, 0.2);
		color: #4eccff;
		padding: 12px 16px;
		text-align: left;
		font-weight: 600;
		border-bottom: 1px solid rgba(78, 204, 255, 0.3);
	}

	.ai-table-cell {
		padding: 10px 16px;
		border-bottom: 1px solid rgba(78, 204, 255, 0.1);
		color: rgba(224, 247, 255, 0.9);
	}

	.ai-table-row:last-child .ai-table-cell {
		border-bottom: none;
	}

	.ai-table-row:nth-child(even) {
		background: rgba(78, 204, 255, 0.05);
	}
}

.ai-divider {
	border: none;
	height: 2px;
	background: linear-gradient(90deg, transparent, rgba(78, 204, 255, 0.5), transparent);
	margin: 20px 0;
	border-radius: 1px;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(15px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes typing-bounce {
	0%,
	80%,
	100% {
		transform: scale(0.3);
		opacity: 0.3;
	}
	40% {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes pulse-ring {
	0% {
		transform: translate(-50%, -50%) scale(0);
		opacity: 1;
	}
	80%,
	100% {
		transform: translate(-50%, -50%) scale(1);
		opacity: 0;
	}
}

@keyframes pulse-active {
	0%,
	100% {
		box-shadow:
			0 0 20px rgba(255, 71, 87, 0.4),
			0 0 40px rgba(255, 107, 122, 0.2);
	}
	50% {
		box-shadow:
			0 0 30px rgba(255, 71, 87, 0.6),
			0 0 60px rgba(255, 107, 122, 0.4);
	}
}

@keyframes glow {
	0%,
	100% {
		text-shadow: 0 0 5px rgba(78, 204, 255, 0.3);
	}
	50% {
		text-shadow: 0 0 20px rgba(78, 204, 255, 0.6);
	}
}

@keyframes fadeInSlide {
	from {
		opacity: 0;
		transform: translateX(-20px) scale(0.9);
	}
	to {
		opacity: 1;
		transform: translateX(0) scale(1);
	}
}

@keyframes gradientShift {
	0%,
	100% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
}

@keyframes loading-bounce {
	0%,
	80%,
	100% {
		transform: scale(0.6);
		opacity: 0.4;
	}
	40% {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes skeleton-loading {
	0% {
		background-position: -200% 0;
	}
	100% {
		background-position: 200% 0;
	}
}

@keyframes card-fade-in {
	from {
		opacity: 0;
		transform: translateY(10px) scale(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes shimmer {
	0% {
		transform: translateX(-100%) rotate(-45deg);
	}
	100% {
		transform: translateX(100%) rotate(-45deg);
	}
}

// 历史记录面板样式
.ai-history-panel {
	position: relative;
	width: 280px;
	height: 100%;
	background: linear-gradient(135deg, rgba(13, 28, 50, 0.98) 0%, rgba(26, 41, 66, 0.95) 100%);
	border: none;
	border-right: 1px solid rgba(32, 156, 238, 0.2);
	border-radius: 20px 0 0 20px;
	backdrop-filter: blur(20px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	z-index: 1001;
	flex-shrink: 0;
}

.ai-history-header {
	padding: 20px 24px;
	// border-bottom: 1px solid rgba(32, 156, 238, 0.2);
	background: linear-gradient(135deg, rgba(32, 156, 238, 0.15) 0%, rgba(78, 204, 255, 0.1) 100%);
	border-radius: 20px 0 0 0;
	display: flex;
	align-items: center;
}

.ai-history-title {
	display: flex;
	align-items: center;
	gap: 8px;
	color: #4eccff;
	font-size: 14px;
	font-weight: 600;
	text-shadow: 0 0 8px rgba(78, 204, 255, 0.3);
}

.ai-history-close {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: rgba(255, 71, 87, 0.1);
	border: 1px solid rgba(255, 71, 87, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;

	&:hover {
		background: rgba(255, 71, 87, 0.2);
		transform: scale(1.1);
	}
}

.ai-history-list {
	flex: 1;
	padding: 10px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 8px;

	&::-webkit-scrollbar {
		width: 4px;
	}

	&::-webkit-scrollbar-track {
		background: rgba(13, 28, 50, 0.3);
		border-radius: 2px;
	}

	&::-webkit-scrollbar-thumb {
		background: linear-gradient(180deg, #209cee, #4eccff);
		border-radius: 2px;
		box-shadow: 0 0 5px rgba(78, 204, 255, 0.3);

		&:hover {
			background: linear-gradient(180deg, #4eccff, #209cee);
		}
	}
}

.ai-new-chat {
	padding: 12px 16px;
	background: linear-gradient(135deg, rgba(46, 213, 115, 0.1) 0%, rgba(78, 204, 255, 0.15) 100%);
	border: 1px solid rgba(46, 213, 115, 0.3);
	border-radius: 12px;
	color: #2ed573;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 10px;

	&:hover {
		background: linear-gradient(135deg, rgba(46, 213, 115, 0.2) 0%, rgba(78, 204, 255, 0.25) 100%);
		border-color: rgba(46, 213, 115, 0.5);
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(46, 213, 115, 0.2);
	}
}

.ai-history-item {
	padding: 10px;
	background: rgba(13, 28, 50, 0.4);
	// border: 1px solid rgba(32, 156, 238, 0.2);
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.3s ease;
	position: relative;

	&:hover {
		background: rgba(32, 156, 238, 0.1);
		border-color: rgba(78, 204, 255, 0.4);
		transform: translateY(-1px);
		.ai-history-item-delete {
			opacity: 1;
		}
	}

	&--active {
		background: linear-gradient(135deg, rgba(78, 204, 255, 0.2) 0%, rgba(32, 156, 238, 0.25) 100%);
		border-color: rgba(78, 204, 255, 0.5);
	}
}

.ai-history-item-title {
	color: var(--deep-blue-text-primary, #e0f7ff);
	font-size: 13px;
	font-weight: 500;
	line-height: 1.4;
	margin-bottom: 4px;
	word-break: break-word;
}

.ai-history-item-time {
	color: rgba(224, 247, 255, 0.6);
	font-size: 11px;
}

.ai-history-item-delete {
	position: absolute;
	top: 8px;
	right: 8px;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: rgba(255, 71, 87, 0.1);
	border: 1px solid rgba(255, 71, 87, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: all 0.3s ease;

	&:hover {
		background: rgba(255, 71, 87, 0.2);
		transform: scale(1.1);
	}
}

.ai-history-empty {
	text-align: center;
	color: rgba(224, 247, 255, 0.5);
	font-size: 12px;
	padding: 20px;
	font-style: italic;
}

.ai-history-loading {
	padding: 15px;
	text-align: center;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.ai-loading-text {
	color: var(--deep-blue-text-secondary, rgba(224, 247, 255, 0.7));
	font-size: 12px;
}

.ai-history-end {
	padding: 15px;
	text-align: center;
	color: var(--deep-blue-text-secondary, rgba(224, 247, 255, 0.5));
	font-size: 12px;
	border-top: 1px dashed rgba(78, 204, 255, 0.2);
	margin-top: 5px;
}

// 历史记录按钮样式
.ai-dialog__history-btn {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: rgba(78, 204, 255, 0.1);
	border: 1px solid rgba(78, 204, 255, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	margin-right: 12px;

	&:hover {
		background: rgba(78, 204, 255, 0.2);
		border-color: rgba(78, 204, 255, 0.5);
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(78, 204, 255, 0.2);
	}
}

// 新的推荐问题样式
.ai-preset-questions-fixed {
	padding: 10px 16px;
	border-bottom: 1px solid rgba(32, 156, 238, 0.2);
	background: linear-gradient(135deg, rgba(13, 28, 50, 0.6) 0%, rgba(26, 41, 66, 0.4) 100%);
	backdrop-filter: blur(10px);
}

.ai-preset-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.ai-preset-title {
	display: flex;
	align-items: center;
	gap: 6px;
	color: #4eccff;
	font-size: 13px;
	font-weight: 600;
	text-shadow: 0 0 8px rgba(78, 204, 255, 0.3);
}

.ai-loading-dots {
	display: flex;
	align-items: center;
	gap: 3px;
	margin-left: 8px;

	span {
		width: 4px;
		height: 4px;
		background: #4eccff;
		border-radius: 50%;
		animation: loading-bounce 1.4s infinite ease-in-out;

		&:nth-child(1) {
			animation-delay: -0.32s;
		}

		&:nth-child(2) {
			animation-delay: -0.16s;
		}
	}
}

.ai-preset-toggle {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: rgba(78, 204, 255, 0.1);
	border: 1px solid rgba(78, 204, 255, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;

	&:hover {
		background: rgba(78, 204, 255, 0.2);
		transform: scale(1.1);
	}

	&--collapsed {
		transform: rotate(180deg);

		&:hover {
			transform: rotate(180deg) scale(1.1);
		}
	}
}

.ai-preset-content {
	transition: all 0.3s ease;
	margin-top: 5px;
}

.ai-preset-loading {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 8px;
}

.ai-preset-skeleton {
	height: 30px;
	border-radius: 10px;
	background: linear-gradient(90deg, rgba(78, 204, 255, 0.1) 25%, rgba(78, 204, 255, 0.2) 50%, rgba(78, 204, 255, 0.1) 75%);
	background-size: 200% 100%;
	animation: skeleton-loading 1.5s infinite;

	&:nth-child(1),
	&:nth-child(4) {
		flex: 0 0 calc(60% - 4px);
	}

	&:nth-child(2),
	&:nth-child(5) {
		flex: 0 0 calc(40% - 4px);
	}

	&:nth-child(3),
	&:nth-child(6) {
		flex: 0 0 calc(50% - 4px);
	}
}

.ai-preset-flow {
	display: block;
	margin-top: 8px;
	line-height: 1;
}

.ai-preset-card {
	padding: 5px 10px;
	background: linear-gradient(135deg, rgba(78, 204, 255, 0.08) 0%, rgba(32, 156, 238, 0.12) 100%);
	// border: 1px solid rgba(78, 204, 255, 0.25);
	border-radius: 12px;
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	overflow: hidden;
	display: inline-flex;
	align-items: center;
	animation: card-fade-in 0.6s ease-out;
	width: fit-content;
	max-width: 100%;
	margin: 4px;

	// 移除固定宽度，让内容自适应
	&--0,
	&--1,
	&--2 {
		flex: none;
		width: auto;
	}

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 2px;
		background: linear-gradient(90deg, #209cee, #4eccff);
		opacity: 0;
		transition: opacity 0.3s ease;
	}

	&:hover {
		background: linear-gradient(135deg, rgba(78, 204, 255, 0.15) 0%, rgba(32, 156, 238, 0.25) 100%);
		border-color: rgba(78, 204, 255, 0.5);
	}

	&:active {
		transform: translateY(0) scale(0.98);
		transition: transform 0.1s ease;
	}
}

.ai-preset-card-text {
	color: #e0f7ff;
	font-size: 13px;
	line-height: 1.4;
	flex: 1;
	word-break: break-word;
	padding-right: 8px;
}

.ai-preset-card-arrow {
	position: absolute;
	top: 12px;
	right: 12px;
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: rgba(78, 204, 255, 0.1);
	display: flex;
	align-items: center;
	justify-content: center;
	color: #4eccff;
	opacity: 0;
	transition: all 0.3s ease;
}
@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}
</style>
